/*
 * WorkAndBuhin.vue の手入力部品・純正部品の作成、重複判定、追加削除を切り出した処理。
 */
import { BuhinDetail } from "@/model/Buhin";
import { isJunseiBuhinCode } from "@/components/work-buhin/WorkBuhinMatch";

export interface FoundJunseiBuhinState {
  shouldApply: boolean;
  appendedBuhinList: BuhinDetail[];
}

export interface FoundJunseiBuhinApplyEffectState {
  shouldApply: boolean;
  appendedBuhinList: BuhinDetail[];
}

export interface ManualBuhinAddClickActionState {
  shouldOpenAddDialog: boolean;
  shouldAddBuhin: boolean;
}

export interface ManualBuhinAddClickCommitApplyEffectState {
  shouldOpenAddDialog: boolean;
  shouldAddBuhin: boolean;
}

export type ManualBuhinAddClickApplyEffectState =
  ManualBuhinAddClickActionState;

export interface ManualBuhinAddApplyState {
  buhin: BuhinDetail;
  manualBuhinNm: string;
  disabledBtnConfirm: boolean;
  scrollTargetId: string;
  shouldUpdateWorkBuhinSelected: boolean;
  workBuhinSelected: number;
  isChange: boolean;
}

export interface ManualBuhinAddCommitApplyEffectState {
  buhin: BuhinDetail;
  manualBuhinNm: string;
  disabledBtnConfirm: boolean;
  scrollTargetId: string;
  shouldUpdateWorkBuhinSelected: boolean;
  workBuhinSelected: number;
  isChange: boolean;
}

export type ManualBuhinAddApplyEffectState = ManualBuhinAddApplyState;

/*
 * createManualBuhin を WorkAndBuhin.vue から分離し、
 * 手入力部品と純正部品の配列操作 を親側置換や単体テストで固定するため。
 */
export function createManualBuhin(
  buhinNm: string,
  buhinShubetsuId: number
): BuhinDetail {
  return {
    buhinCd: null,
    buhinId: null,
    buhinNm,
    quantity: 1,
    buhinShubetsuId,
    buhinPriceZn: 0,
    buhinPriceZk: 0,
    kingakuZn: 0,
    kingakuZk: 0,
    isDelete: 0
  };
}

/*
 * hasManualBuhinName を WorkAndBuhin.vue から分離し、
 * 手入力部品と純正部品の配列操作 を親側置換や単体テストで固定するため。
 */
export function hasManualBuhinName(
  manualBuhinList: BuhinDetail[],
  buhinNm: string
): boolean {
  return manualBuhinList.some((buhin: BuhinDetail) => buhin.buhinNm == buhinNm);
}

/*
 * buildManualBuhinAddClickActionState を WorkAndBuhin.vue から分離し、
 * 手入力部品追加ボタン押下時の disabled / 未入力 / 重複名分岐を固定するため。
 */
export function buildManualBuhinAddClickActionState(params: {
  disabledBtnNoBuhin: boolean;
  manualBuhinNm: string | null | undefined;
  manualBuhinList: BuhinDetail[];
}): ManualBuhinAddClickActionState {
  if (params.disabledBtnNoBuhin || !params.manualBuhinNm) {
    return {
      shouldOpenAddDialog: false,
      shouldAddBuhin: false
    };
  }
  if (hasManualBuhinName(params.manualBuhinList, params.manualBuhinNm)) {
    return {
      shouldOpenAddDialog: true,
      shouldAddBuhin: false
    };
  }
  return {
    shouldOpenAddDialog: false,
    shouldAddBuhin: true
  };
}

/*
 * buildManualBuhinAddClickApplyEffectState を WorkAndBuhin.vue から分離し、
 * 手入力部品追加ボタン押下後の重複 dialog 表示と追加実行条件を固定するため。
 */
export function buildManualBuhinAddClickApplyEffectState(
  actionState: ManualBuhinAddClickActionState
): ManualBuhinAddClickApplyEffectState {
  return {
    shouldOpenAddDialog: actionState.shouldOpenAddDialog,
    shouldAddBuhin: actionState.shouldAddBuhin
  };
}

/*
 * buildManualBuhinAddClickCommitApplyEffectState を WorkAndBuhin.vue から分離し、
 * 手入力部品追加ボタン押下後に component が実行する重複 dialog 表示と追加実行条件を固定するため。
 */
export function buildManualBuhinAddClickCommitApplyEffectState(
  effectState: ManualBuhinAddClickApplyEffectState
): ManualBuhinAddClickCommitApplyEffectState {
  return {
    shouldOpenAddDialog: effectState.shouldOpenAddDialog,
    shouldAddBuhin: effectState.shouldAddBuhin
  };
}

/*
 * buildManualBuhinAddApplyState を WorkAndBuhin.vue から分離し、
 * 手入力部品追加後に反映する入力欄 clear、confirm 状態、作業選択状態を固定するため。
 */
export function buildManualBuhinAddApplyState(params: {
  manualBuhinNm: string;
  buhinShubetsuId: number;
  activeWorkIndex: number;
  doneStatus: number;
}): ManualBuhinAddApplyState {
  return {
    buhin: createManualBuhin(params.manualBuhinNm, params.buhinShubetsuId),
    manualBuhinNm: "",
    disabledBtnConfirm: false,
    scrollTargetId: "list-buhin",
    shouldUpdateWorkBuhinSelected: params.activeWorkIndex > -1,
    workBuhinSelected: params.doneStatus,
    isChange: params.activeWorkIndex > -1
  };
}

/*
 * buildManualBuhinAddApplyEffectState を WorkAndBuhin.vue から分離し、
 * 手入力部品追加後の各 list 追加、入力欄 clear、作業選択状態反映条件を固定するため。
 */
export function buildManualBuhinAddApplyEffectState(
  applyState: ManualBuhinAddApplyState
): ManualBuhinAddApplyEffectState {
  return {
    buhin: applyState.buhin,
    manualBuhinNm: applyState.manualBuhinNm,
    disabledBtnConfirm: applyState.disabledBtnConfirm,
    scrollTargetId: applyState.scrollTargetId,
    shouldUpdateWorkBuhinSelected: applyState.shouldUpdateWorkBuhinSelected,
    workBuhinSelected: applyState.workBuhinSelected,
    isChange: applyState.isChange
  };
}

/*
 * buildManualBuhinAddCommitApplyEffectState を WorkAndBuhin.vue から分離し、
 * 手入力部品追加後に component が反映する list 追加、入力欄 clear、作業選択状態を固定するため。
 */
export function buildManualBuhinAddCommitApplyEffectState(
  effectState: ManualBuhinAddApplyEffectState
): ManualBuhinAddCommitApplyEffectState {
  return {
    buhin: effectState.buhin,
    manualBuhinNm: effectState.manualBuhinNm,
    disabledBtnConfirm: effectState.disabledBtnConfirm,
    scrollTargetId: effectState.scrollTargetId,
    shouldUpdateWorkBuhinSelected: effectState.shouldUpdateWorkBuhinSelected,
    workBuhinSelected: effectState.workBuhinSelected,
    isChange: effectState.isChange
  };
}

/*
 * findManualOrJunseiBuhinIndex を WorkAndBuhin.vue から分離し、
 * 手入力部品と純正部品の配列操作 を親側置換や単体テストで固定するため。
 */
export function findManualOrJunseiBuhinIndex(
  manualBuhinList: BuhinDetail[],
  buhin: BuhinDetail
): number {
  if (isJunseiBuhinCode(buhin.buhinCd)) {
    return manualBuhinList.findIndex(
      (manualBuhin: BuhinDetail) =>
        manualBuhin.buhinCd == buhin.buhinCd &&
        manualBuhin.buhinNm == buhin.buhinNm
    );
  }
  return manualBuhinList.findIndex(
    (manualBuhin: BuhinDetail) => manualBuhin.buhinNm == buhin.buhinNm
  );
}

/*
 * removeManualOrJunseiBuhin を WorkAndBuhin.vue から分離し、
 * 手入力部品と純正部品の配列操作 を親側置換や単体テストで固定するため。
 */
export function removeManualOrJunseiBuhin(
  manualBuhinList: BuhinDetail[],
  buhin: BuhinDetail
): BuhinDetail | null {
  const index = findManualOrJunseiBuhinIndex(manualBuhinList, buhin);
  if (index === -1) {
    return null;
  }
  const removed = manualBuhinList.splice(index, 1);
  return removed.length > 0 ? removed[0] : null;
}

/*
 * getDeletedManualBuhinKey を WorkAndBuhin.vue から分離し、
 * 手入力部品と純正部品の配列操作 を親側置換や単体テストで固定するため。
 */
export function getDeletedManualBuhinKey(buhin: BuhinDetail): any {
  if (isJunseiBuhinCode(buhin.buhinCd)) {
    return buhin.buhinCd;
  }
  return buhin.id;
}

/*
 * createJunseiBuhinFromSearchResult を WorkAndBuhin.vue から分離し、
 * 手入力部品と純正部品の配列操作 を親側置換や単体テストで固定するため。
 */
export function createJunseiBuhinFromSearchResult(item: any): BuhinDetail {
  return {
    buhinShubetsuId: 0,
    buhinId: null,
    buhinCd: item.buhinCd,
    buhinNm: item.buhinNm,
    quantityStep: 1,
    quantity: item.quantityStep || 0,
    buhinPriceZn: item.kingakuZn,
    buhinPriceZk: 0,
    kingakuZn: item.kingakuZn,
    kingakuZk: 0,
    isDelete: 0,
    canJunseiFree: item.canJunseiFree
  };
}

/*
 * appendUniqueJunseiBuhin を WorkAndBuhin.vue から分離し、
 * 手入力部品と純正部品の配列操作 を親側置換や単体テストで固定するため。
 */
export function appendUniqueJunseiBuhin(
  manualBuhinList: BuhinDetail[],
  items: any[]
): BuhinDetail[] {
  const appended: BuhinDetail[] = [];
  items.forEach((item: any) => {
    const junseiBuhin = createJunseiBuhinFromSearchResult(item);
    const exists = manualBuhinList.some(
      (buhin: BuhinDetail) =>
        buhin.buhinCd === junseiBuhin.buhinCd &&
        buhin.buhinNm === junseiBuhin.buhinNm
    );
    if (!exists) {
      manualBuhinList.push(junseiBuhin);
      appended.push(junseiBuhin);
    }
  });
  return appended;
}

/*
 * buildFoundJunseiBuhinState を WorkAndBuhin.vue から分離し、
 * 純正部品検索結果を手入力部品 list へ反映する条件と追加結果を親側置換や単体テストで固定するため。
 */
export function buildFoundJunseiBuhinState(params: {
  manualBuhinList: BuhinDetail[];
  items: any[];
}): FoundJunseiBuhinState {
  const appendedBuhinList = appendUniqueJunseiBuhin(
    params.manualBuhinList,
    params.items
  );
  return {
    shouldApply: appendedBuhinList.length > 0,
    appendedBuhinList
  };
}

/*
 * buildFoundJunseiBuhinApplyEffectState を WorkAndBuhin.vue から分離し、
 * 純正部品検索結果追加後の反映有無と追加済み部品 list を固定するため。
 */
export function buildFoundJunseiBuhinApplyEffectState(
  foundState: FoundJunseiBuhinState
): FoundJunseiBuhinApplyEffectState {
  return {
    shouldApply: foundState.shouldApply,
    appendedBuhinList: foundState.appendedBuhinList
  };
}

/*
 * appendUniqueSelectedJunseiBuhin を WorkAndBuhin.vue から分離し、
 * 手入力部品と純正部品の配列操作 を親側置換や単体テストで固定するため。
 */
export function appendUniqueSelectedJunseiBuhin(
  selectedBuhinList: BuhinDetail[],
  items: BuhinDetail[]
): void {
  items.forEach((buhin: BuhinDetail) => {
    const exists = selectedBuhinList.some(
      (selected: BuhinDetail) =>
        selected.buhinCd === buhin.buhinCd &&
        selected.buhinNm === buhin.buhinNm
    );
    if (!exists) {
      selectedBuhinList.push(buhin);
    }
  });
}
